import React, { useEffect, useState } from 'react';
import { Layout, Menu, Space, Button } from 'antd';
import {
  DashboardOutlined,
  SwapOutlined,
  ShopOutlined,
  UserOutlined,
  FlagOutlined,
  SettingOutlined,
  BarChartOutlined,
  AuditOutlined,
  SafetyOutlined,
  SlidersOutlined,
  ToolOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const { Sider, Header, Content } = Layout;

function openKeysForPath(path) {
  if (
    path.startsWith('/admin/moderation')
    || path.startsWith('/admin/deals')
    || path.startsWith('/admin/merchants')
    || path.startsWith('/admin/categories')
  ) {
    return ['content'];
  }
  if (path.startsWith('/admin/users') || path.startsWith('/admin/audit-logs')) {
    return ['community'];
  }
  if (
    path.startsWith('/admin/feature-flags')
    || path.startsWith('/admin/affiliate-programs')
    || path.startsWith('/admin/settings')
    || path.startsWith('/admin/notifications')
  ) {
    return ['configure'];
  }
  return [];
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState(() => openKeysForPath(location.pathname));
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!collapsed) {
      setOpenKeys(openKeysForPath(location.pathname));
    }
  }, [location.pathname, collapsed]);

  const menuItems = [
    { key: '/admin', icon: <DashboardOutlined />, label: 'Dashboard' },
    {
      key: 'content',
      icon: <GlobalOutlined />,
      label: 'Content',
      children: [
        { key: '/admin/moderation', icon: <FlagOutlined />, label: 'Moderation' },
        { key: '/admin/deals', icon: <SwapOutlined />, label: 'Deals' },
        { key: '/admin/merchants', icon: <ShopOutlined />, label: 'Merchants' },
        { key: '/admin/categories', icon: <AppstoreOutlined />, label: 'Categories' },
      ],
    },
    {
      key: 'community',
      icon: <SafetyOutlined />,
      label: 'Community',
      children: [
        { key: '/admin/users', icon: <UserOutlined />, label: 'Users' },
        { key: '/admin/audit-logs', icon: <AuditOutlined />, label: 'Audit Logs' },
      ],
    },
    {
      key: 'configure',
      icon: <SlidersOutlined />,
      label: 'Configure',
      children: [
        { key: '/admin/feature-flags', icon: <SettingOutlined />, label: 'Feature Flags' },
        { key: '/admin/affiliate-programs', icon: <BarChartOutlined />, label: 'Affiliate Programs' },
        { key: '/admin/notifications', icon: <BellOutlined />, label: 'Notifications' },
        { key: '/admin/settings', icon: <ToolOutlined />, label: 'Settings' },
      ],
    },
    { key: '/admin/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--df-surface)' }}>
      <Sider
        width={240}
        collapsed={collapsed}
        collapsible
        trigger={null}
        onCollapse={setCollapsed}
        style={{
          background: 'var(--df-primary)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0,
          overflow: 'auto',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/admin')}
          aria-label="Admin home"
          style={{
            height: 64,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            paddingLeft: collapsed ? 0 : 18,
            border: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent',
            cursor: 'pointer',
            gap: 10,
          }}
        >
          <span
            className="df-brand__mark"
            style={{ width: 28, height: 28, fontSize: 11, borderRadius: 7 }}
            aria-hidden="true"
          >
            DF
          </span>
          {!collapsed && (
            <span
              style={{
                color: '#F4F7FA',
                fontSize: 15,
                fontWeight: 700,
                fontFamily: 'var(--df-font-display)',
                letterSpacing: '-0.02em',
              }}
            >
              DealFusion Admin
            </span>
          )}
        </button>

        <Menu
          theme="dark"
          mode="inline"
          items={menuItems}
          selectedKeys={[location.pathname === '/admin' ? '/admin' : location.pathname]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 14,
            marginTop: 8,
          }}
          onClick={({ key }) => {
            if (!key.startsWith('/')) return;
            navigate(key);
          }}
        />
      </Sider>

      <Layout style={{ background: 'transparent' }}>
        <Header
          style={{
            background: 'var(--df-surface-elevated)',
            padding: '0 20px',
            borderBottom: '1px solid var(--df-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 56,
            lineHeight: '56px',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ width: 44, height: 44 }}
          />
          <Space size={12}>
            <Button
              type="text"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
              style={{ fontWeight: 600, color: 'var(--df-primary)' }}
            >
              View site
            </Button>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(11, 58, 92, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--df-primary)',
                fontWeight: 700,
                fontSize: 13,
                fontFamily: 'var(--df-font-display)',
              }}
              aria-label="Admin account"
            >
              {user?.email?.charAt(0)?.toUpperCase() || user?.displayName?.charAt(0)?.toUpperCase() || 'A'}
            </span>
          </Space>
        </Header>

        <Content style={{ padding: 24, background: 'var(--df-surface)', minHeight: 'calc(100vh - 56px)' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
